import React, { useState, useEffect, useRef } from 'react';
import { 
  Camera, 
  Upload, 
  Image, 
  Calendar, 
  TrendingUp, 
  Shield, 
  Eye, 
  EyeOff,
  Download,
  Share2,
  Heart,
  Star,
  AlertTriangle,
  CheckCircle,
  Clock,
  Target,
  Award,
  Users
} from 'lucide-react';

interface ProgressPhoto {
  id: string;
  type: 'goal' | 'current' | 'progress';
  category: 'front' | 'side' | 'back' | 'flexed' | 'relaxed';
  timestamp: Date;
  url: string;
  analysis?: {
    bodyComposition: {
      muscleDefinition: number;
      bodyFat: number;
      posture: number;
      symmetry: number;
      progress: number;
    };
    measurements: {
      chest: number;
      waist: number;
      hips: number;
      arms: number;
      thighs: number;
    };
    insights: string[];
    recommendations: string[];
  };
  privacy: {
    visibility: 'private' | 'coach_only' | 'community' | 'public';
    marketingUse: boolean;
  };
}

interface ProgressTimeline {
  id: string;
  name: string;
  startDate: Date;
  endDate?: Date;
  photos: ProgressPhoto[];
  milestones: Array<{
    date: Date;
    photoId: string;
    progress: number;
    achievements: string[];
    notes: string;
  }>;
  progressSummary: {
    totalProgress: number;
    timeSpan: number;
    keyChanges: string[];
    recommendations: string[];
  };
}

interface PhotoProgressPanelProps {
  userId: string;
  userProfile: any;
  onPhotoUpload?: (photo: ProgressPhoto) => void;
  onTimelineCreate?: (timeline: ProgressTimeline) => void;
  onMarketingConsent?: (consent: any) => void;
}

export const PhotoProgressPanel: React.FC<PhotoProgressPanelProps> = ({
  userId,
  userProfile,
  onPhotoUpload,
  onTimelineCreate,
  onMarketingConsent
}) => {
  const [activeTab, setActiveTab] = useState<'upload' | 'timeline' | 'analysis' | 'consent'>('upload');
  const [photos, setPhotos] = useState<ProgressPhoto[]>([]);
  const [timelines, setTimelines] = useState<ProgressTimeline[]>([]);
  const [selectedPhoto, setSelectedPhoto] = useState<ProgressPhoto | null>(null);
  const [uploading, setUploading] = useState(false);
  const [privacySettings, setPrivacySettings] = useState({
    visibility: 'private' as const,
    marketingUse: false,
    dataRetention: 365
  });
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    loadProgressData();
  }, []);

  const loadProgressData = async () => {
    // Mock data - in production, this would come from the PhotoProgressBee
    setPhotos([
      {
        id: '1',
        type: 'current',
        category: 'front',
        timestamp: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000),
        url: '/progress-photos/current-front.jpg',
        analysis: {
          bodyComposition: {
            muscleDefinition: 6,
            bodyFat: 25,
            posture: 7,
            symmetry: 8,
            progress: 5
          },
          measurements: {
            chest: 36,
            waist: 32,
            hips: 38,
            arms: 12,
            thighs: 22
          },
          insights: ['Good posture improvement', 'Increased muscle definition in arms'],
          recommendations: ['Continue current routine', 'Focus on core strengthening']
        },
        privacy: {
          visibility: 'private',
          marketingUse: false
        }
      }
    ]);

    setTimelines([
      {
        id: '1',
        name: '12-Week Transformation',
        startDate: new Date(Date.now() - 84 * 24 * 60 * 60 * 1000),
        photos: [],
        milestones: [
          {
            date: new Date(Date.now() - 84 * 24 * 60 * 60 * 1000),
            photoId: 'baseline',
            progress: 0,
            achievements: ['Started fitness journey'],
            notes: 'Baseline measurements and photos'
          },
          {
            date: new Date(Date.now() - 42 * 24 * 60 * 60 * 1000),
            photoId: 'midpoint',
            progress: 50,
            achievements: ['Lost 5 lbs', 'Improved posture'],
            notes: 'Halfway point - seeing great progress!'
          }
        ],
        progressSummary: {
          totalProgress: 75,
          timeSpan: 84,
          keyChanges: ['Improved posture', 'Increased muscle definition', 'Better body composition'],
          recommendations: ['Continue current routine', 'Add more protein to diet']
        }
      }
    ]);
  };

  const handlePhotoUpload = async (file: File, type: string, category: string) => {
    setUploading(true);
    
    try {
      // In production, this would upload to secure storage and call PhotoProgressBee
      const newPhoto: ProgressPhoto = {
        id: `photo_${Date.now()}`,
        type: type as any,
        category: category as any,
        timestamp: new Date(),
        url: URL.createObjectURL(file),
        privacy: privacySettings
      };

      setPhotos(prev => [newPhoto, ...prev]);
      
      if (onPhotoUpload) {
        onPhotoUpload(newPhoto);
      }

      // Simulate analysis
      setTimeout(() => {
        setPhotos(prev => prev.map(photo => 
          photo.id === newPhoto.id 
            ? {
                ...photo,
                analysis: {
                  bodyComposition: {
                    muscleDefinition: Math.floor(Math.random() * 5) + 5,
                    bodyFat: Math.floor(Math.random() * 10) + 20,
                    posture: Math.floor(Math.random() * 3) + 6,
                    symmetry: Math.floor(Math.random() * 3) + 6,
                    progress: Math.floor(Math.random() * 5) + 5
                  },
                  measurements: {
                    chest: 36,
                    waist: 32,
                    hips: 38,
                    arms: 12,
                    thighs: 22
                  },
                  insights: ['Good progress', 'Maintain current routine'],
                  recommendations: ['Continue strength training', 'Focus on nutrition']
                }
              }
            : photo
        ));
      }, 2000);

    } catch (error) {
      console.error('Error uploading photo:', error);
    } finally {
      setUploading(false);
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // In production, this would show a modal to select type and category
      handlePhotoUpload(file, 'progress', 'front');
    }
  };

  const getProgressColor = (progress: number) => {
    if (progress >= 8) return 'text-green-600';
    if (progress >= 6) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getProgressIcon = (progress: number) => {
    if (progress >= 8) return <CheckCircle className="w-4 h-4 text-green-600" />;
    if (progress >= 6) return <Target className="w-4 h-4 text-yellow-600" />;
    return <AlertTriangle className="w-4 h-4 text-red-600" />;
  };

  return (
    <div className="flex flex-col h-full sculptd-card-elevated sculptd-sheen" style={{
      boxShadow: 'var(--shadow-card-separation)',
      border: '1px solid rgba(0, 0, 0, 0.08)'
    }}>
      {/* Header */}
      <div className="flex items-center justify-end sculptd-container-padding border-b" style={{ borderColor: 'var(--color-border)' }}>
        <div className="flex items-center space-x-3">
          <Shield className="w-4 h-4" style={{ color: 'var(--color-text-muted)' }} />
          <span className="text-sm" style={{ color: 'var(--color-text-muted)', textShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>
            {privacySettings.visibility === 'private' ? 'Private' : 'Shared'}
          </span>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b" style={{ borderColor: 'var(--color-border)' }}>
        <button
          onClick={() => setActiveTab('upload')}
          className={`flex-1 py-3 px-4 text-sm font-medium ${
            activeTab === 'upload'
              ? 'text-blue-600 border-b-2 border-blue-600'
              : ''
          }`}
          style={activeTab !== 'upload' ? { 
            color: 'var(--color-text-muted)'
          } : {}}
        >
          Upload
        </button>
        <button
          onClick={() => setActiveTab('timeline')}
          className={`flex-1 py-3 px-4 text-sm font-medium ${
            activeTab === 'timeline'
              ? 'text-blue-600 border-b-2 border-blue-600'
              : ''
          }`}
          style={activeTab !== 'timeline' ? { 
            color: 'var(--color-text-muted)'
          } : {}}
        >
          Timeline
        </button>
        <button
          onClick={() => setActiveTab('analysis')}
          className={`flex-1 py-3 px-4 text-sm font-medium ${
            activeTab === 'analysis'
              ? 'text-blue-600 border-b-2 border-blue-600'
              : ''
          }`}
          style={activeTab !== 'analysis' ? { 
            color: 'var(--color-text-muted)'
          } : {}}
        >
          Analysis
        </button>
        <button
          onClick={() => setActiveTab('consent')}
          className={`flex-1 py-3 px-4 text-sm font-medium ${
            activeTab === 'consent'
              ? 'text-blue-600 border-b-2 border-blue-600'
              : ''
          }`}
          style={activeTab !== 'consent' ? { 
            color: 'var(--color-text-muted)'
          } : {}}
        >
          Consent
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto sculptd-container-padding">
        {activeTab === 'upload' && (
          <div className="space-y-6">
            {/* Upload Section */}
            <div className="sculptd-card-elevated sculptd-sheen border-2 border-dashed rounded-lg sculptd-section-padding text-center" style={{
              boxShadow: 'var(--shadow-card-separation)',
              borderColor: 'var(--color-charcoal)'
            }}>
              <Camera className="w-12 h-12 mx-auto mb-4" style={{ color: 'var(--color-text-muted)' }} />
              <h3 className="text-lg font-medium mb-2" style={{ 
                color: 'var(--color-text)',
                textShadow: '0 1px 2px rgba(0,0,0,0.05)'
              }}>Upload Progress Photo</h3>
              <p className="mb-4" style={{ color: 'var(--color-text-muted)' }}>
                Take or upload a photo to track your progress. We recommend consistent lighting and angles.
              </p>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileSelect}
                className="hidden"
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="sculptd-cta-button px-6 py-3 text-white rounded-lg disabled:opacity-50 transition-colors"
                style={{ 
                  background: 'var(--gradient-cta-primary)',
                  boxShadow: 'var(--shadow-lg)'
                }}
                onMouseEnter={(e) => {
                  (e.target as HTMLElement).style.background = 'var(--gradient-accent-button)';
                  (e.target as HTMLElement).style.boxShadow = 'var(--shadow-xl)';
                }}
                onMouseLeave={(e) => {
                  (e.target as HTMLElement).style.background = 'var(--gradient-cta-primary)';
                  (e.target as HTMLElement).style.boxShadow = 'var(--shadow-lg)';
                }}
              >
                {uploading ? 'Uploading...' : 'Choose Photo'}
              </button>
            </div>

            {/* Photo Guidelines */}
            <div className="border rounded-lg sculptd-card-padding" style={{ 
              background: 'var(--color-primary-light)', 
              borderColor: 'var(--color-primary)' 
            }}>
              <h4 className="font-medium mb-2" style={{ color: 'var(--color-text)' }}>Photo Guidelines</h4>
              <ul className="text-sm space-y-1" style={{ color: 'var(--color-text-muted)', textShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>
                <li>• Use consistent lighting and background</li>
                <li>• Take photos at the same time of day</li>
                <li>• Wear the same or similar clothing</li>
                <li>• Include front, side, and back views</li>
                <li>• Take photos every 2-4 weeks for best results</li>
              </ul>
            </div>

            {/* Privacy Settings */}
            <div className="border rounded-lg sculptd-card-padding" style={{ background: 'var(--color-background)', borderColor: 'var(--color-border)' }}>
              <h4 className="font-medium mb-3" style={{ color: 'var(--color-text)' }}>Privacy Settings</h4>
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium mb-1" style={{ color: 'var(--color-text)', textShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>
                    Photo Visibility
                  </label>
                  <select
                    value={privacySettings.visibility}
                    onChange={(e) => setPrivacySettings(prev => ({ ...prev, visibility: e.target.value as any }))}
                    className="w-full border rounded-lg px-3 py-2 text-sm"
                    style={{ borderColor: 'var(--color-border)', background: 'white', color: 'var(--color-text)' }}
                  >
                    <option value="private">Private (Only You)</option>
                    <option value="coach_only">Coach Only</option>
                    <option value="community">Community</option>
                    <option value="public">Public</option>
                  </select>
                </div>
                <div className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    id="marketingUse"
                    checked={privacySettings.marketingUse}
                    onChange={(e) => setPrivacySettings(prev => ({ ...prev, marketingUse: e.target.checked }))}
                    className="rounded"
                    style={{ borderColor: 'var(--color-border)' }}
                  />
                  <label htmlFor="marketingUse" className="text-sm" style={{ color: 'var(--color-text)', textShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>
                    Allow use in marketing materials (with compensation)
                  </label>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'timeline' && (
          <div className="space-y-6">
            {timelines.map((timeline) => (
              <div key={timeline.id} className="sculptd-card-elevated sculptd-sheen rounded-lg sculptd-card-padding" style={{
                boxShadow: 'var(--shadow-card-separation)'
              }}>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold" style={{ color: 'var(--color-text)' }}>{timeline.name}</h3>
                  <div className="flex items-center space-x-3">
                    <TrendingUp className="w-4 h-4 text-green-600" />
                    <span className="text-sm font-medium text-green-600" style={{ textShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>
                      {timeline.progressSummary.totalProgress}% Progress
                    </span>
                  </div>
                </div>
                
                <div className="mb-4">
                  <div className="flex justify-between text-sm mb-1" style={{ color: 'var(--color-text-muted)', textShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>
                    <span>Progress</span>
                    <span>{timeline.progressSummary.totalProgress}%</span>
                  </div>
                  <div className="w-full rounded-full h-2" style={{ background: 'var(--color-background-secondary)' }}>
                    <div
                      className="h-2 rounded-full"
                      style={{ 
                        width: `${timeline.progressSummary.totalProgress}%`,
                        background: 'var(--gradient-cta-primary)'
                      }}
                    ></div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="font-medium text-gray-900 mb-2">Key Changes</h4>
                    <ul className="text-sm space-y-1" style={{ color: 'var(--color-text-muted)', textShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>
                      {timeline.progressSummary.keyChanges.map((change, index) => (
                        <li key={index} className="flex items-center space-x-3">
                          <CheckCircle className="w-3 h-3 text-green-600" />
                          <span>{change}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-900 mb-2">Recommendations</h4>
                    <ul className="text-sm space-y-1" style={{ color: 'var(--color-text-muted)', textShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>
                      {timeline.progressSummary.recommendations.map((rec, index) => (
                        <li key={index} className="flex items-center space-x-3">
                          <Target className="w-3 h-3 text-blue-600" />
                          <span>{rec}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                <div className="mt-4 pt-4 border-t" style={{ borderColor: 'var(--color-border)' }}>
                  <h4 className="font-medium text-gray-900 mb-2">Milestones</h4>
                  <div className="space-y-2">
                    {timeline.milestones.map((milestone, index) => (
                      <div key={index} className="flex items-center space-x-3 p-2 rounded" style={{ background: 'var(--color-background)' }}>
                        <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ 
                          background: 'var(--color-primary-light)' 
                        }}>
                          <span className="text-xs font-medium" style={{ color: 'var(--color-primary)' }}>{index + 1}</span>
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center space-x-2">
                            <span className="text-sm font-medium" style={{ color: 'var(--color-text)', textShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>
                              {milestone.date.toLocaleDateString()}
                            </span>
                            <span className="text-xs text-gray-500">
                              {milestone.progress}% progress
                            </span>
                          </div>
                          <p className="text-sm" style={{ color: 'var(--color-text-muted)', textShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>{milestone.notes}</p>
                          <div className="flex flex-wrap gap-1 mt-1">
                            {milestone.achievements.map((achievement, i) => (
                              <span key={i} className="px-2 py-1 text-xs rounded-full" style={{
                                background: 'var(--color-success)/10',
                                color: 'var(--color-success)'
                              }}>
                                {achievement}
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {activeTab === 'analysis' && (
          <div className="space-y-6">
            {photos.map((photo) => (
              <div key={photo.id} className="sculptd-card-elevated sculptd-sheen rounded-lg sculptd-card-padding" style={{
                boxShadow: '0 4px 16px rgba(0,0,0,0.08), 0 2px 8px rgba(0,0,0,0.04)'
              }}>
                <div className="flex items-start space-x-4">
                  <div className="w-24 h-24 rounded-lg overflow-hidden" style={{ background: 'var(--color-background-secondary)' }}>
                    <img
                      src={photo.url}
                      alt={`${photo.type} ${photo.category}`}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="font-medium text-gray-900 capitalize">
                        {photo.type} - {photo.category}
                      </h3>
                      <span className="text-sm" style={{ color: 'var(--color-text-muted)', textShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>
                        {photo.timestamp.toLocaleDateString()}
                      </span>
                    </div>
                    
                    {photo.analysis && (
                      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-4">
                        <div className="text-center">
                          <div className="flex items-center justify-center mb-1">
                            {getProgressIcon(photo.analysis.bodyComposition.muscleDefinition)}
                          </div>
                          <div className={`text-sm font-medium ${getProgressColor(photo.analysis.bodyComposition.muscleDefinition)}`}>
                            {photo.analysis.bodyComposition.muscleDefinition}/10
                          </div>
                          <div className="text-xs text-gray-500">Muscle</div>
                        </div>
                        <div className="text-center">
                          <div className="flex items-center justify-center mb-1">
                            {getProgressIcon(photo.analysis.bodyComposition.posture)}
                          </div>
                          <div className={`text-sm font-medium ${getProgressColor(photo.analysis.bodyComposition.posture)}`}>
                            {photo.analysis.bodyComposition.posture}/10
                          </div>
                          <div className="text-xs text-gray-500">Posture</div>
                        </div>
                        <div className="text-center">
                          <div className="flex items-center justify-center mb-1">
                            {getProgressIcon(photo.analysis.bodyComposition.symmetry)}
                          </div>
                          <div className={`text-sm font-medium ${getProgressColor(photo.analysis.bodyComposition.symmetry)}`}>
                            {photo.analysis.bodyComposition.symmetry}/10
                          </div>
                          <div className="text-xs text-gray-500">Symmetry</div>
                        </div>
                        <div className="text-center">
                          <div className="flex items-center justify-center mb-1">
                            {getProgressIcon(photo.analysis.bodyComposition.progress)}
                          </div>
                          <div className={`text-sm font-medium ${getProgressColor(photo.analysis.bodyComposition.progress)}`}>
                            {photo.analysis.bodyComposition.progress}/10
                          </div>
                          <div className="text-xs text-gray-500">Progress</div>
                        </div>
                        <div className="text-center">
                          <div className="text-sm font-medium" style={{ color: 'var(--color-text)', textShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>
                            {photo.analysis.bodyComposition.bodyFat}%
                          </div>
                          <div className="text-xs text-gray-500">Body Fat</div>
                        </div>
                      </div>
                    )}

                    {photo.analysis && (
                      <div className="space-y-2">
                        <div>
                          <h4 className="text-sm font-medium mb-1" style={{ color: 'var(--color-text)', textShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>Insights</h4>
                          <ul className="text-sm space-y-1" style={{ color: 'var(--color-text-muted)', textShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>
                            {photo.analysis.insights.map((insight, index) => (
                              <li key={index} className="flex items-center space-x-2">
                                <Star className="w-3 h-3 text-yellow-500" />
                                <span>{insight}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                        <div>
                          <h4 className="text-sm font-medium mb-1" style={{ color: 'var(--color-text)', textShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>Recommendations</h4>
                          <ul className="text-sm space-y-1" style={{ color: 'var(--color-text-muted)', textShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>
                            {photo.analysis.recommendations.map((rec, index) => (
                              <li key={index} className="flex items-center space-x-2">
                                <Target className="w-3 h-3 text-blue-500" />
                                <span>{rec}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {activeTab === 'consent' && (
          <div className="space-y-6">
            <div className="border rounded-lg sculptd-card-padding" style={{ 
              background: 'var(--color-primary-light)', 
              borderColor: 'var(--color-primary)' 
            }}>
              <h3 className="font-medium mb-2" style={{ color: 'var(--color-text)' }}>Marketing Consent</h3>
              <p className="text-sm mb-4" style={{ color: 'var(--color-text-muted)', textShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>
                You can choose to allow your progress photos to be used in marketing materials. 
                This helps inspire other women on their fitness journey and may include compensation.
              </p>
              <div className="space-y-3">
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="marketingConsent"
                    className="rounded"
                    style={{ borderColor: 'var(--color-border)' }}
                  />
                  <label htmlFor="marketingConsent" className="text-sm" style={{ color: 'var(--color-primary)', textShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>
                    I consent to the use of my progress photos in marketing materials
                  </label>
                </div>
                <div className="text-xs text-blue-700">
                  • You can withdraw consent at any time<br/>
                  • Photos will be anonymized if requested<br/>
                  • Compensation may include free premium features<br/>
                  • You retain full control over your data
                </div>
              </div>
            </div>

            <div className="border rounded-lg sculptd-card-padding" style={{ background: 'var(--color-background)', borderColor: 'var(--color-border)' }}>
              <h3 className="font-medium text-gray-900 mb-2">Data Protection</h3>
              <div className="space-y-2 text-sm" style={{ color: 'var(--color-text-muted)', textShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>
                <div className="flex items-center space-x-2">
                  <Shield className="w-4 h-4 text-green-600" />
                  <span>All photos are encrypted and stored securely</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Eye className="w-4 h-4 text-blue-600" />
                  <span>You control who can see your photos</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Clock className="w-4 h-4 text-purple-600" />
                  <span>Data retention: {privacySettings.dataRetention} days</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Download className="w-4 h-4 text-orange-600" />
                  <span>You can download your data anytime</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PhotoProgressPanel;
